import { Link } from "react-router-dom";
import { Heart, MapPin, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { TAG_LABELS } from "@/lib/outings/utils";
import type { OutingTag } from "@/types/outings";
import { track } from "@/lib/analytics";
import { formatDateBucket, distanceKmBucket } from "@/lib/analytics/helpers";
import { activityDetailPath } from "@/lib/paths";

interface OutingCardProps {
  id: string;
  title: string;
  coverImageUrl: string | null;
  dateLabel: string;
  distanceLabel: string | null;
  badge: OutingTag | null;
  saved: boolean;
  onToggleSave: () => void;
  /** Raw values for analytics bucketing */
  startDate?: string;
  distanceKm?: number | null;
}

const BADGE_STYLES: Record<OutingTag, string> = {
  barnvänligt: "bg-amber-100 text-amber-800",
  "plocka-själv": "bg-emerald-100 text-emerald-800",
  kultur: "bg-violet-100 text-violet-800",
  gratis: "bg-sky-100 text-sky-800",
};

export function OutingCard({
  id,
  title,
  coverImageUrl,
  dateLabel,
  distanceLabel,
  badge,
  saved,
  onToggleSave,
  startDate,
  distanceKm: rawDistanceKm,
}: OutingCardProps) {
  const handleCardClick = () => {
    track("outing_card_click", {
      outing_id: id,
      badge: badge ?? null,
      date_bucket: startDate ? formatDateBucket(startDate) : "upcoming",
      distance_km_bucket: distanceKmBucket(rawDistanceKm ?? null),
    }, "explore");
  };

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      {/* Image */}
      <Link to={activityDetailPath(id)} onClick={handleCardClick} className="relative aspect-video overflow-hidden bg-muted">
        {coverImageUrl ? (
          <img
            src={coverImageUrl}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              // Hide broken image, show fallback
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement?.classList.add('outing-img-error');
            }}
          />
        ) : null}
        {/* Fallback – shown when no image or image fails to load */}
        {!coverImageUrl && (
          <div className="flex h-full w-full items-center justify-center bg-secondary">
            <span className="text-3xl">🌿</span>
          </div>
        )}
        {coverImageUrl && (
          <div className="hidden h-full w-full items-center justify-center bg-secondary [.outing-img-error>&]:flex">
            <span className="text-3xl">🌿</span>
          </div>
        )}

        {/* Badge overlay */}
        {badge && (
          <span
            className={cn(
              "absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-xs font-medium",
              BADGE_STYLES[badge]
            )}
          >
            {TAG_LABELS[badge]}
          </span>
        )}
      </Link>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <Link to={activityDetailPath(id)}>
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground group-hover:text-primary transition-colors">
            {title}
          </h3>
        </Link>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {dateLabel}
          </span>
          {distanceLabel && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {distanceLabel}
            </span>
          )}
        </div>

        {/* Save button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            track("outing_save_toggle", { outing_id: id, saved: !saved }, "explore");
            onToggleSave();
          }}
          className={cn(
            "mt-1 flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
            saved
              ? "bg-primary/10 text-primary"
              : "bg-secondary text-muted-foreground hover:bg-secondary/80"
          )}
        >
          <Heart className={cn("h-3.5 w-3.5", saved && "fill-current")} />
          {saved ? "Sparad" : "Spara"}
        </button>
      </div>
    </div>
  );
}
